import { ChevronDown, ChevronUp, Eye, EyeOff, Lock, Trash2, Unlock } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { layersInOrder } from '../../../utils/invitationStyle';
import { FOCUS_RING } from '../constants';
import { PanelHeading } from '../controls';
import { itemLabel } from '../designUtils';
import { displayText } from '../../../utils/affix';

const iconBtn = cn(
  'h-8 w-8 shrink-0 rounded-lg flex items-center justify-center text-ink hover:bg-cream disabled:opacity-30 disabled:hover:bg-transparent',
  FOCUS_RING
);

/** Layers tool: every item in paint order, top of the list in front. */
export const LayersPanel = ({ design, selectedId, onSelect, onPatch, onMove, onDelete }) => {
  const layers = layersInOrder(design.text_fields, design.elements).reverse();
  return (
    <div className="flex flex-col gap-3">
      <PanelHeading>Layers</PanelHeading>
      <p className="text-xs text-[#6B665C]">Top of the list is in front. Click to select.</p>
      <ul className="flex flex-col gap-1.5" data-testid="layers-list">
        {layers.map(({ kind, item }, i) => {
          const found = { kind, item };
          const label = itemLabel(found);
          const selected = item.id === selectedId;
          const deletable = kind === 'element' || item.custom;
          return (
            <li
              key={item.id}
              className={cn(
                'min-h-[48px] rounded-xl flex items-center gap-0.5 pl-1 pr-1',
                selected ? 'gradient-border-strong' : 'border-[1.5px] border-[#EEEBE4] bg-white'
              )}
              data-testid={`layer-${item.id}`}
            >
              <button
                type="button"
                onClick={() => onSelect(item.id)}
                aria-pressed={selected}
                className={cn('flex-1 min-w-0 text-left flex flex-col px-2 py-1.5 rounded-lg', FOCUS_RING)}
              >
                <span className={cn('text-[13px] font-bold truncate', item.hidden ? 'text-[#9C978C]' : 'text-ink')}>{label}</span>
                {kind === 'text' ? (
                  <span dir="auto" className="font-hebrew text-xs text-[#6B665C] truncate">
                    {displayText(item, design.text_fields) || '(empty)'}
                  </span>
                ) : (
                  <span className="text-xs text-[#6B665C]">{item.type === 'image' ? 'Photo' : 'Element'}</span>
                )}
              </button>
              <button
                type="button"
                className={iconBtn}
                onClick={() => onMove(item.id, 1)}
                disabled={i === 0}
                aria-label={`Move ${label} forward`}
              >
                <ChevronUp className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                className={iconBtn}
                onClick={() => onMove(item.id, -1)}
                disabled={i === layers.length - 1}
                aria-label={`Move ${label} back`}
              >
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                className={iconBtn}
                onClick={() => onPatch(item.id, { locked: !item.locked })}
                aria-label={`${item.locked ? 'Unlock' : 'Lock'} ${label}`}
                aria-pressed={Boolean(item.locked)}
              >
                {item.locked ? <Lock className="h-4 w-4" aria-hidden="true" /> : <Unlock className="h-4 w-4 text-[#B8B2A6]" aria-hidden="true" />}
              </button>
              <button
                type="button"
                className={iconBtn}
                onClick={() => onPatch(item.id, { hidden: !item.hidden })}
                aria-label={`${item.hidden ? 'Show' : 'Hide'} ${label}`}
                aria-pressed={Boolean(item.hidden)}
                data-testid={`layer-eye-${item.id}`}
              >
                {item.hidden ? <EyeOff className="h-4 w-4 text-[#B8B2A6]" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
              </button>
              {deletable ? (
                <button type="button" className={cn(iconBtn, 'text-[#B0003A]')} onClick={() => onDelete(item.id)} aria-label={`Delete ${label}`}>
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              ) : (
                <span className="w-8 shrink-0" aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ul>
      {layers.length === 0 && <p className="text-sm text-[#6B665C]">Nothing on the page yet.</p>}
    </div>
  );
};

export default LayersPanel;
